import {Component, Input, EventEmitter, Output, OnDestroy, ViewChild, ElementRef} from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';

/**
 * This component encapsulated the MatCheckbox with Tyler styling
 * This component supports content projection in exactly the same way as MatCheckbox.
 * In addition, a string that is projected into MatCheckbox will automatcally be translated.
 *
 * @input labelPosition: determines where the label appears, 'before' or 'after'. Defaults to 'after'.
 * @input required: whether the checkbox is required. Defaults to false.
 * @input name: the name of the checkbox (for forms). Defaults to 'tyl-checkbox-#'.
 * @input id: the id of the checkbox. Defaults to 'tyl-checkbox-#'.
 * @input indeterminate: the indeterminate state of the checkbox. Defaults to false.
 * @input disabled: whether the control is disabled. Defaults to false.
 * @input disableRipple: whether the control ripples. Defaults to false.
 * @input color: the color theme to apply. Defaults to 'accent'.
 * @input checked: the varibale the checkbox binds to.
 * @output change: this event emitter emits the boolean for its state when it changes. (used for Angular similarity)
 * @output checkedChange: this event emitter emits the boolean for its state when it changes. (custom for two-way binding)
 */
@Component({
    selector: 'tyl-checkbox',
    templateUrl: './tyl-checkbox.component.html',
    styleUrls: [
        './tyl-checkbox.component.css'
    ]
})
export class TylCheckboxComponent implements OnDestroy {

    // Static Properties
    static nameIterator = 0;
    static idIterator = 0;

    // Interface Properties for MatCheckbox
    @Input() labelPosition = 'after';
    @Input() required = false;
    @Input() name = 'tyl-checkbox-' + TylCheckboxComponent.nameIterator++;
    @Input() indeterminate = false;
    @Input() id = 'tyl-checkbox-' + TylCheckboxComponent.idIterator++;
    @Input() disabled = false;
    @Input() disableRipple = false;
    @Input() color = 'accent';
    @Input() value = true;
    @Output() change = new EventEmitter<boolean>();

    // Custom Interface Properties
    private _checked = false;
    @Output() checkedChange = new EventEmitter<boolean>();

    // Local Properties
    @ViewChild('innerCheckbox', { read: MatCheckbox, static: true }) innerCheckbox: MatCheckbox;
    @ViewChild('contentRef', { read: ElementRef, static: true }) contentRef: ElementRef;

    // Class Functions
    get checked(): boolean {
        return this._checked;
    }

    @Input() set checked(value: boolean) {
        this._checked = value;
        this.checkedChange.emit(this._checked);
    }

    ngOnDestroy() {
        // POST: subtracts from the iterators the values of this control
        TylCheckboxComponent.nameIterator -= 1;
        TylCheckboxComponent.idIterator -= 1;
    }

    // Event Functions

    // Methods
    public focus() {
        this.innerCheckbox.focus('keyboard');
    }

    public hasContent() {
        return !!this.contentRef.nativeElement.innerHTML;
    }
}
